import './FeedBundle.sass';

import React, { FC, useCallback, useMemo } from 'react';
import { useRouter } from 'react-router5';

import { FeedBundleProps } from './FeedBundle.interface';
import { Block, Button } from '$uikit';
import { UnitLink, UnitName, UnitPhoto } from '$shared/components';
import { useTranslator, useUserInfo } from '$hooks';
import { formatCurrency, statReachGoal } from '$utils';
import { IcAngleRight16 } from '$assets';
import { FEED } from '$shared/constants/pages';
import { showModal } from '$navigation/helper';

export const FeedBundle: FC<FeedBundleProps> = (props) => {
  const { bundle, showMoreButton } = props;
  const router = useRouter();
  const t = useTranslator();
  const user = useUserInfo(bundle.userId);
  const photo = useMemo(
    () =>
      [bundle.coverUrl, bundle.thumbnail]
        .filter(Boolean)
        .map((u) => `url(${u})`)
        .join(', '),
    [bundle],
  );

  const handleClick = useCallback(() => {
    showModal('bundle' + bundle.id);
    statReachGoal('bundles_feed_item_click', {
      id: bundle.id,
    });
  }, [bundle]);

  const handleMoreClick = useCallback(() => {
    router.navigate(FEED, { section: 'bundles' });
    statReachGoal('bundles_feed_more_click', {
      id: bundle.id,
    });
  }, [router]);

  const text = useMemo(() => {
    if (bundle.description.length > 110) {
      return bundle.description.slice(0, 90) + '...';
    }

    return bundle.description;
  }, [bundle]);

  return (
    <Block>
      <div className="FeedBundle" onClick={handleClick}>
        <div className="FeedBundle__info">
          <UnitLink className="FeedBundle__author" unit={user}>
            <UnitPhoto
              nftIndicator={false}
              onlineIndicator={false}
              size={'small'}
              unit={user}
            />
            <div>
              <div className="FeedBundle__author__name">
                <UnitName unit={user} />
              </div>
              <div className="FeedBundle__author__info">
                {t('feed_bundle_label', {
                  price: formatCurrency(bundle.price, bundle.currency),
                })}
              </div>
            </div>
          </UnitLink>
          <div className="FeedBundle__name">{bundle.name}</div>
          <div className="FeedBundle__description">{text}</div>
          <div className="FeedBundle__preview" style={{ backgroundImage: photo }} />
          <div className="FeedBundle__action">
            <Button size="medium" stretched>
              {t('feed_bundle_button')}
            </Button>
          </div>
        </div>
      </div>
      {showMoreButton && (
        <div className="FeedBundle__more" onClick={handleMoreClick}>
          {t('feed_bundle_more')}
          <IcAngleRight16 />
        </div>
      )}
    </Block>
  );
};
